/*
 *  -----------------------------------------------------------------------
 *    <copyright file="kri-scores-chart.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'hooks';
import { HC_COLORS_STANDARD, OpraColours } from 'interfaces/opra-chart.interface';
import { PanelListFields } from 'queries/generated/fragments.generated';
import DumbbellChart from 'views/components/core/charts/dumbbell-chart';
import PanelFrame from 'views/components/core/panel-frame';

interface IKriScoresChart {
    scoringData: any;
    panels: PanelListFields[];
    siteInfo: any;
}

const KriScoresChart = ({ scoringData, panels, siteInfo }: IKriScoresChart) => {
    const { t } = useTranslation();
    const [chartData, setChartData] = useState<any[]>([]);

    useEffect(() => {
        if (scoringData && panels && siteInfo) {
            const plots = panels
                .filter((item: any) => item.panelType === 'MODEL' && !item.modelParams.type.match('TREND'))
                .map((item: any) => {
                    let averageForPanel = 0;
                    let sum = 0;
                    let amount = 0;
                    scoringData.siteScores.forEach((el: any) => {
                        const score = el.indicatorScores.find((e: any) => e.panelId === item.id);

                        if (score) {
                            sum += score.score;
                            amount += 1;
                        }
                    });
                    averageForPanel = sum / amount;
                    const site = scoringData.siteScores.find((e: any) => e.siteId === siteInfo.Fields['Site.id']);

                    if (site == null) return;

                    const score = site.indicatorScores.find((e: any) => e.panelId === item.id);
                    return {
                        label: item.graph.plotTitle,
                        instanceValue: +score?.score ?? 0,
                        color: score?.color,
                        averageValue: averageForPanel,
                        siteId: siteInfo.Fields['Site.id'],
                    };
                })
                .filter((item: any) => item);

            const series = plots.map((item: any) => {
                const siteColor = item.color ?? null;
                const color =
                    siteColor === 1
                        ? HC_COLORS_STANDARD[OpraColours.Red]
                        : siteColor === 2
                          ? HC_COLORS_STANDARD[OpraColours.Amber]
                          : siteColor === 3
                            ? HC_COLORS_STANDARD[OpraColours.Green]
                            : HC_COLORS_STANDARD[OpraColours.LightGrey];
                const colorString =
                    siteColor === 1 ? 'Red' : siteColor === 2 ? 'Amber' : siteColor === 3 ? 'Green' : 'Grey';
                const tooltip = `<span><b>Site RAG Status:</b> ${colorString}</span><br/>
                    <span><b>Site Score:</b> ${item.instanceValue.toFixed(2)}</span><br/>
                    <span><b>Study Mean Score:</b> ${item.averageValue.toFixed(2)}</span><br/>`;
                return {
                    tooltip: {
                        pointFormat: tooltip,
                    },
                    color,
                    plots: [item],
                };
            });

            setChartData(series);
        }
    }, [scoringData, panels, siteInfo]);

    return (
        <PanelFrame title={t('ra:KRIScoreChart')} toggleLegend={false}>
            {(getRef: any) => (
                <DumbbellChart
                    getRef={getRef}
                    title={t('ra:KRIScoreChart')}
                    hidePlotTitle
                    yAxisTitle={t('admin:panel')}
                    series={chartData}
                />
            )}
        </PanelFrame>
    );
};

export default KriScoresChart;
